import { ChangeEvent, useCallback, useEffect, useMemo } from "react";
import { FetcherWithComponents, useFetcher } from "react-router-dom";
import _ from "lodash";

import InputFieldWithValidation from "components/InputFieldWithValidation";
import Spinner from "components/Spinner";

import { useActiveCoupons } from "routes/data-routes/billing-data/coupons/active-coupons";
import {
  CheckCouponsDataRoutePath,
  CheckCouponsFetcherDataType
} from "routes/data-routes/billing-data/coupons/check-coupons/route.meta";

import { BillingInputName, CouponInputName } from "v2/dataroutes/BillingData";

import { Coupon, CouponError } from "types/Coupons";

import { cx } from "utils";


function isCoupon(data: Coupon | CouponError | null | undefined): data is Coupon {
  return Boolean(data) && "coupon_name" in (data as any) && !(data as any).error;
}

function getReplacementWarning(validatedCoupon: Coupon | CouponError | null | undefined, activeCoupons: Coupon[] | null | undefined): string | undefined {
  if (!isCoupon(validatedCoupon) || !activeCoupons?.length) return undefined;

  const existingOfSameType = activeCoupons.find(
    c => c.discount_type === validatedCoupon.discount_type
      && c.coupon_code !== validatedCoupon.coupon_code
  );

  if (!existingOfSameType) return undefined;

  const name = existingOfSameType.coupon_name || existingOfSameType.coupon_code;
  return `This will replace your existing "${name}" discount.`;
}


interface ICouponCodeInputProps {
  /**
   * Used to check coupons against a target plan, like when upgrading. Without this, if a coupon
   * has plan restrictions, those will be compared to the current plan. In such cases it can happen that the UI
   * will show a coupon as valid (compared to the current plan), but after upgrading, it won't be added to the target
   * plan if that doesn't allow the coupon in question.
   */
  targetPlanSlug?: string;
  className?: string;
  inputClassName?: string;
  hideLabel?: boolean;
  autoFocus?: boolean;
  required?: boolean;

  fetcher?: FetcherWithComponents<CheckCouponsFetcherDataType>;
  onUserInput?: () => void;
}

export default function CouponCodeInput(
  {
    targetPlanSlug,
    className,
    inputClassName,
    hideLabel,
    autoFocus,
    required,
    fetcher,
    onUserInput
  }: ICouponCodeInputProps
) {
  const { activeCoupons } = useActiveCoupons();

  const internalFetcher = useFetcher<CheckCouponsFetcherDataType>();
  const couponFetcher = useMemo(() => {
    return fetcher ?? internalFetcher;
  }, [ fetcher, internalFetcher ]);

  const debouncedCouponCheck = useMemo(() => {
    return _.debounce(
      (form: HTMLFormElement | null, fetcher: typeof couponFetcher) => {
        fetcher.submit(form, {
          method: "GET",
          action: CheckCouponsDataRoutePath,
        });
      },
      500
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => debouncedCouponCheck.cancel();
  }, [ debouncedCouponCheck ]);

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onUserInput?.();
      debouncedCouponCheck.cancel();
      debouncedCouponCheck(e.target.form, couponFetcher);
    },
    [ debouncedCouponCheck, couponFetcher, onUserInput ]
  );

  const replacementWarning = useMemo(
    () => getReplacementWarning(couponFetcher.data?.coupon, activeCoupons),
    [couponFetcher.data, activeCoupons]
  );

  return (
    <div className={ cx("min-h-[90px]", className) }>
      <input type="hidden" name={ BillingInputName.targetPlanSlug } value={ targetPlanSlug }/>
      <input type="hidden" name="required" value={ required ? "true" : "false" } />
      { activeCoupons?.map((coupon, idx) =>
        <input key={ idx } type="hidden" name={ CouponInputName.activeCouponCode } value={ coupon.coupon_code }/>
      ) }
      <InputFieldWithValidation
        label={ hideLabel ? undefined : "Coupon Code" }
        placeholder="Enter a coupon code"
        type="text"
        defaultValue={ couponFetcher.data?.coupon?.coupon_code }
        name={ CouponInputName.couponCode }
        className={ inputClassName }
        onChange={ onChange }
        TrailingElement={ (couponFetcher.state === "loading") ? <Spinner size="XXS"/> : undefined }
        errorMessage={ couponFetcher.data ? (couponFetcher.data.coupon as CouponError).error : "" }
        successMessage={ couponFetcher.data?.coupon?.coupon_name }
        required={ required }
        autoFocus={ autoFocus }
      />
      {replacementWarning && (
        <p className="text-sm text-amber-600 mt-1">{replacementWarning}</p>
      )}
    </div>
  );
};
